import clsx from 'clsx'
import CustomField from 'common/components/CustomField'
import {Form, Formik, FormikHelpers, FormikValues} from 'formik'
import {FC, useState} from 'react'
import {Alert} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {changeUserPassword} from 'setup/redux/Actions/School/User/user'
import * as Yup from 'yup'

type Props = {
  callback?: () => void
}

const ChangePassword: FC<Props> = ({callback}) => {
  const intl = useIntl()

  const schema = Yup.object().shape({
    oldPassword: Yup.string()
      .label(intl.formatMessage({id: 'GENERAL.OLD_PASSWORD'}))
      .required(),
    password: Yup.string()
      .label(intl.formatMessage({id: 'GENERAL.NEW_PASSWORD'}))
      .required(),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')])
      .label(intl.formatMessage({id: 'GENERAL.CONFIRM_PASSWORD'}))
      .required(),
  })

  const initialValues = {
    oldPassword: '',
    password: '',
    confirmPassword: '',
  }

  const [success, setSuccess] = useState('')
  const dispatch = useDispatch()

  const submit = (values: FormikValues, actions: FormikHelpers<any>) => {
    actions.setSubmitting(true)
    actions.setStatus('')
    setSuccess('')

    const onSuccess = () => {
      actions.setSubmitting(false)
      setSuccess('Password Changed')
      actions.resetForm()
      if (callback) {
        callback()
      }
    }

    const onFailure = (res: any) => {
      actions.setStatus(res.message ?? 'Some error occurred. Please try again.')
      actions.setSubmitting(false)
    }

    dispatch(changeUserPassword(values.oldPassword, values.password, onSuccess, onFailure))
  }

  return (
    <>
      <div className={clsx({card: !callback})}>
        <div className={clsx({'card-body': !callback})}>
          <Formik validationSchema={schema} initialValues={initialValues} onSubmit={submit}>
            {({status, isSubmitting}) => (
              <Form>
                <div className='w-100'>
                  <div className='pb-10 pb-lg-12'>
                    <h2 className='fw-bolder text-dark'>
                      {intl.formatMessage({id: 'PROFILE.CHANGE_PASSWORD'})}
                    </h2>
                  </div>

                  {callback && (
                    <Alert variant='info'>
                      Your password is too old. Please, change your password.
                    </Alert>
                  )}

                  {status && (
                    <div className='mb-lg-15 alert alert-danger'>
                      <div className='alert-text font-weight-bold'>{status}</div>
                    </div>
                  )}

                  {success && (
                    <div className='mb-lg-15 alert alert-success'>
                      <div className='alert-text font-weight-bold'>{success}</div>
                    </div>
                  )}

                  <CustomField
                    type='password'
                    name='oldPassword'
                    label='GENERAL.OLD_PASSWORD'
                    fieldClassName='form-control-lg form-control-solid'
                    required
                  />

                  <CustomField
                    type='password'
                    name='password'
                    label='GENERAL.NEW_PASSWORD'
                    fieldClassName='form-control-lg form-control-solid'
                    required
                  />

                  <CustomField
                    type='password'
                    name='confirmPassword'
                    label='GENERAL.CONFIRM_PASSWORD'
                    fieldClassName='form-control-lg form-control-solid'
                    required
                  />
                </div>

                <div className={clsx({'d-grid': callback})}>
                  <button type='submit' className='btn btn-primary' disabled={isSubmitting}>
                    {isSubmitting && (
                      <>
                        <span
                          className='spinner-border spinner-border-sm me-2'
                          role='status'
                          aria-hidden='true'
                        />
                        <span className='visually-hidden'>Loading...</span>
                      </>
                    )}
                    {intl.formatMessage({id: 'GENERAL.SAVE'})}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  )
}

export default ChangePassword
